import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampaignMapWidget = () => {
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');
  const [viewMode, setViewMode] = useState('clusters');

  const campaignData = [
    {
      id: 'camp_001',
      title: 'Climate Policy Misinformation',
      region: 'Western Europe',
      severity: 'high',
      confidence: 0.87,
      platforms: ['Facebook', 'Twitter', 'TikTok'],
      reach: 2400000,
      growth: '+15%',
      coordinates: { lat: 50.8503, lng: 4.3517 },
      status: 'active',
      lastUpdate: new Date(Date.now() - 1800000)
    },
    {
      id: 'camp_002',
      title: 'Election Integrity Claims',
      region: 'Central Europe',
      severity: 'critical',
      confidence: 0.92,
      platforms: ['Telegram', 'Facebook'],
      reach: 1800000,
      growth: '+28%',
      coordinates: { lat: 52.5200, lng: 13.4050 },
      status: 'escalated',
      lastUpdate: new Date(Date.now() - 900000)
    },
    {
      id: 'camp_003',
      title: 'Healthcare Conspiracy',
      region: 'Southern Europe',
      severity: 'medium',
      confidence: 0.74,
      platforms: ['WhatsApp', 'Instagram'],
      reach: 950000,
      growth: '+8%',
      coordinates: { lat: 41.9028, lng: 12.4964 },
      status: 'monitoring',
      lastUpdate: new Date(Date.now() - 3600000)
    },
    {
      id: 'camp_004',
      title: 'Economic Disinformation',
      region: 'Northern Europe',
      severity: 'high',
      confidence: 0.81,
      platforms: ['LinkedIn', 'Twitter'],
      reach: 1200000,
      growth: '+12%',
      coordinates: { lat: 59.3293, lng: 18.0686 },
      status: 'active',
      lastUpdate: new Date(Date.now() - 2700000)
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical': return 'AlertTriangle';
      case 'escalated': return 'TrendingUp';
      case 'active': return 'Activity';
      case 'monitoring': return 'Eye';
      default: return 'Circle';
    }
  };

  const filteredCampaigns = campaignData?.filter(campaign => 
    selectedRegion === 'all' || campaign?.region === selectedRegion
  );

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Map" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Live Campaign Mapping</h3>
              <p className="text-sm text-muted-foreground">Real-time threat visualization across EU regions</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'clusters' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('clusters')}
            >
              Clusters
            </Button>
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('timeline')}
            >
              Timeline
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Regions</option>
            <option value="Western Europe">Western Europe</option>
            <option value="Central Europe">Central Europe</option>
            <option value="Southern Europe">Southern Europe</option>
            <option value="Northern Europe">Northern Europe</option>
            <option value="Eastern Europe">Eastern Europe</option>
          </select>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>

          <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 rounded-md">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-xs font-medium text-success">Live Updates</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Map Visualization Area */}
        <div className="relative bg-muted/30 rounded-lg h-96 mb-6 overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="EU Campaign Map"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=50.8503,4.3517&z=4&output=embed"
            className="rounded-lg"
          />
          
          {/* Overlay Campaign Markers */}
          <div className="absolute inset-0 pointer-events-none">
            {filteredCampaigns?.map((campaign, index) => (
              <div
                key={campaign?.id}
                className="absolute pointer-events-auto"
                style={{
                  left: `${20 + (index * 15)}%`,
                  top: `${30 + (index * 10)}%`
                }}
              >
                <div className={`w-4 h-4 ${getSeverityColor(campaign?.severity)} rounded-full animate-pulse cursor-pointer`} />
              </div>
            ))}
          </div>
        </div>

        {/* Campaign List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">Active Campaigns ({filteredCampaigns?.length})</h4>
            <Button variant="ghost" size="sm" iconName="RefreshCw">
              Refresh
            </Button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredCampaigns?.map((campaign) => (
              <div
                key={campaign?.id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 ${getSeverityColor(campaign?.severity)} rounded-full`} />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h5 className="text-sm font-medium text-foreground">{campaign?.title}</h5>
                      <Icon name={getStatusIcon(campaign?.status)} size={14} className="text-muted-foreground" />
                    </div>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-xs text-muted-foreground">{campaign?.region}</span>
                      <span className="text-xs text-muted-foreground">
                        Confidence: {(campaign?.confidence * 100)?.toFixed(0)}%
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Reach: {(campaign?.reach / 1000000)?.toFixed(1)}M
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium ${campaign?.growth?.startsWith('+') ? 'text-red-600' : 'text-green-600'}`}>
                    {campaign?.growth}
                  </span>
                  <Button variant="ghost" size="icon" iconName="ChevronRight" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignMapWidget;